import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
	SysAdminCompanyTestingWorkflowWithBatchTypeIds,
} from "node-library/dist/portal";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	FrontendCompanyTestingWorkflowInfoHierarchyNodes,
	FrontendCompanyWorkflow,
	FrontendCompanyWorkflowSteps,
} from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";
import {
	Severity,
	TestingWorkflowInfoHierarchyNodeType,
} from "node-library/dist/constants";

import { Tab, Tabs } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { GreenButton } from "../../../../common-components/button";
import { CustomDrawer } from "../../../../common-components/drawers/drawer";
import UnsavedChangesModal from "../../../../common-components/modals/unsavedChangesModal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { buildStepTree } from "../../../../helpers/workflowHelper";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../requestHelper/sendRequest";
import { WorkflowStepItem } from "../../batchDetailsPage/components/workflowComponents/workflowStepItem";
import WorkflowCell from "./workflowCell";
import AddOrEditWorkflowStepModal from "./addOrEditWorkflowStepModal";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { EditIcon } from "../../../../common-components/svgIcons";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	workflowDetails: FrontendCompanyWorkflow;
	setWorkflowDetails: React.Dispatch<
		SetStateAction<FrontendCompanyWorkflow | undefined>
	>;
}

export default function WorkflowStepTab(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { company, setCompany, user, workflowDetails, setWorkflowDetails } =
		props;

	const [renderingStepId, setRenderingStepId] = useState<string>("");

	const [selectedStep, setSelectedStep] = useState<
		FrontendCompanyWorkflowSteps | undefined
	>();
	const [addOrEditStepModalOpen, setAddOrEditStepModalOpen] =
		useState<boolean>(false);

	const [loading, setLoading] = useState<boolean>(false);

	async function addOrEditWorkflowStep(
		workflowStep: FrontendCompanyWorkflowSteps,
		isEdit: boolean,
		stepRootNodeInfo?: {
			stepRootNodeName?: string;
			stepRootNodeKey?: string;
			stepRootNodeType?: TestingWorkflowInfoHierarchyNodeType;
		},
	) {
		try {
			let response;
			setLoading(true);
			let stepRootNodeName: string | undefined;
			let stepRootNodeKey: string | undefined;
			let stepRootNodeType: TestingWorkflowInfoHierarchyNodeType | undefined;
			if (stepRootNodeInfo) {
				stepRootNodeName = stepRootNodeInfo.stepRootNodeName;
				stepRootNodeKey = stepRootNodeInfo.stepRootNodeKey;
				stepRootNodeType = stepRootNodeInfo.stepRootNodeType;
			}
			if (isEdit) {
				response = await sendPOSTRequest("/structures/edit-workflow-step", {
					workflowId: workflowDetails.workflowId,
					updatedWorkflowStep: workflowStep,
					stepRootNodeName,
					stepRootNodeKey,
					stepRootNodeType,
				});

				const rootInfoNodeToUpdate:
					| FrontendCompanyTestingWorkflowInfoHierarchyNodes
					| undefined = response.rootInfoNodeToUpdate;

				let updatedStep: FrontendCompanyWorkflowSteps;
				if (rootInfoNodeToUpdate) {
					if (!workflowStep.rootHierarchyNode) {
						updatedStep = {
							...workflowStep,
							rootHierarchyNode: rootInfoNodeToUpdate,
						};
					} else {
						updatedStep = {
							...workflowStep,
							rootHierarchyNode: {
								...workflowStep.rootHierarchyNode,
								nodeName: rootInfoNodeToUpdate.nodeName,
								nodeKey: rootInfoNodeToUpdate.nodeKey,
								nodeType: rootInfoNodeToUpdate.nodeType,
							},
						};
					}
				} else {
					updatedStep = { ...workflowStep, rootHierarchyNode: undefined };
				}
				setWorkflowDetails({
					...workflowDetails,
					workflowSteps: workflowDetails.workflowSteps.map((step) =>
						step.stepId === updatedStep.stepId ? updatedStep : step,
					),
				});
			} else {
				response = await sendPOSTRequest(
					"/structures/create-new-workflow-step",
					{
						workflowId: workflowDetails.workflowId,
						newWorkflowStep: workflowStep,
						stepRootNodeName,
						stepRootNodeKey,
						stepRootNodeType,
					},
				);
				const newStepId: string = response.newStepId;
				const rootInfoNodeToCreate:
					| FrontendCompanyTestingWorkflowInfoHierarchyNodes
					| undefined = response.rootInfoNodeToCreate;

				const finalResult: FrontendCompanyWorkflowSteps = {
					...workflowStep,
					stepId: newStepId,
					rootHierarchyNode: rootInfoNodeToCreate,
				};

				setWorkflowDetails({
					...workflowDetails,
					workflowSteps: [...workflowDetails.workflowSteps, finalResult],
				});
			}

			openSnackbar(
				translation("WorkflowStepUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);

			setAddOrEditStepModalOpen(false);
		} catch (error: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	//STEPS TAB
	const stepTree = useMemo(() => {
		if (!workflowDetails?.workflowSteps) {
			return { rootNodes: [], childrenMap: new Map() };
		}
		return buildStepTree(workflowDetails.workflowSteps);
	}, [workflowDetails]);

	const renderSteps = (
		steps: FrontendCompanyWorkflowSteps[],
		childrenMap: Map<string, Array<FrontendCompanyWorkflowSteps>>,
		level = 0,
	): JSX.Element[] => {
		if (!steps || steps.length === 0 || !workflowDetails) return [];

		return steps.map((step) => {
			const children = childrenMap.get(step.stepId);
			return (
				<div key={step.stepId} style={{ marginLeft: level * 20 }}>
					<WorkflowStepItem
						step={step}
						workflow={workflowDetails}
						// Ensure these state variables (currentStep, stepToRender)
						// are defined in your component state!
						currentStep={undefined}
						renderingStepId={renderingStepId}
						setStepToRender={() => setRenderingStepId(step.stepId)}
					/>
					{children &&
						children.length > 0 &&
						renderSteps(children, childrenMap, level + 1)}
				</div>
			);
		});
	};
	const createWorkflowButton: React.ReactNode = (
		<GreenButton
			key="create-result-field-button"
			id="create-result-field-button"
			size="flex"
			children={
				<>
					<AddIcon></AddIcon>
					{translation("Step")}
				</>
			}
			onClick={() => {
				setSelectedStep(undefined);
				setAddOrEditStepModalOpen(true);
			}}
		></GreenButton>
	);
	const workflowStepsTab: React.ReactNode = (
		<>
			<div>{createWorkflowButton}</div>
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
				<div>{renderSteps(stepTree.rootNodes, stepTree.childrenMap)}</div>
				<div>
					<GreenButton
						key="edit-workflow-step-button"
						id="edit-workflow-step-button"
						size="flex"
						children={
							<>
								<EditIcon></EditIcon>
								{translation("Step")}
							</>
						}
						onClick={() => {
							setSelectedStep(
								workflowDetails.workflowSteps.find((step) => {
									return step.stepId === renderingStepId;
								}),
							);
							setAddOrEditStepModalOpen(true);
						}}
					></GreenButton>
				</div>
			</div>
		</>
	);

	return (
		<>
			{workflowStepsTab}
			<AddOrEditWorkflowStepModal
				company={company}
				addOrEditWorkflowStep={addOrEditWorkflowStep}
				open={addOrEditStepModalOpen}
				setOpen={setAddOrEditStepModalOpen}
				loading={loading}
				otherWorkflowSteps={workflowDetails.workflowSteps.filter((step) => {
					return step.stepId !== selectedStep?.stepId;
				})}
				workflowStep={selectedStep}
			/>
		</>
	);
}
